import axios from 'axios'
import { getUploadSignal } from './uploadCancellation'
import { isAllowedImageType } from './imageUpload'

const BACKEND_URL = (import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000').replace(/\/$/, '')
// AUTH_URL: the auth subdomain (e.g. https://auth.wetook.net). Empty on the
// auth app itself so that 401s redirect to /login rather than looping back.
const AUTH_URL = (import.meta.env.VITE_AUTH_URL || '').replace(/\/$/, '')

const api = axios.create({
  baseURL: (BACKEND_URL || '') + '/api/v1',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // send HttpOnly session cookie cross-origin
})

export { BACKEND_URL }

// Friendly, user-safe message for any server-side failure. Never expose the
// raw backend `detail` for 5xx — those can leak stack-trace fragments, ORM
// internals, or schema names. 4xx detail strings are deliberate user messages
// and are passed through unchanged.
export const GENERIC_SERVER_ERROR = 'Something went wrong. Please try again.'
export const GENERIC_NETWORK_ERROR = 'Network error. Check your connection and try again.'
const MEDIA_UPLOAD_LIMIT_BYTES = 100 * 1024 * 1024
const AUDIO_UPLOAD_LIMIT_BYTES = 50 * 1024 * 1024

function assertUploadSize(file: File, maxBytes: number) {
  if (file.size <= maxBytes) return
  const actualMb = (file.size / (1024 * 1024)).toFixed(1)
  throw new Error(`File is ${actualMb} MB. Maximum allowed size is ${maxBytes / (1024 * 1024)} MB.`)
}

/**
 * Did this request never reach the server?
 *
 * NOT `!error.response`. `sanitizeError` below synthesizes a response with
 * `status: 0` for network, CORS and offline failures, so by the time any
 * caller sees the error it always HAS a response object — and every
 * "if (!err.response) we must be offline" check silently never fires. That
 * mistake is invisible in testing, because it only shows up when the network
 * is genuinely gone, which is the one condition a passing test suite does not
 * reproduce. Ask through this instead.
 *
 * Requires `error.request` too (fixed 2026-09-25), matching the distinction
 * `sanitizeError` already makes below: an axios error with NEITHER a
 * response NOR a request never actually reached the network layer — the
 * request was never dispatched, so this is a bug in the calling code (e.g.
 * a thrown config error), not a connectivity problem. `sanitizeError`
 * deliberately leaves that case unstamped; before this fix, `isNetworkError`
 * disagreed and called it a network error anyway — a caller like
 * kdsOffline.ts's flushMoves would then retry a real bug forever instead of
 * surfacing it, exactly the class of "these two functions were supposed to
 * agree and didn't" this file's own comment warns about for `!err.response`.
 */
export const isNetworkError = (error: any): boolean =>
  (!error?.response && !!error?.request) || error?.response?.status === 0

export function sanitizeError(error: any) {
  // No HTTP response → network/CORS/offline failure.
  if (!error.response) {
    if (error.response === undefined && !error.request) return error
    error.response = {
      status: 0,
      data: { detail: GENERIC_NETWORK_ERROR },
    }
    return error
  }
  const status = error.response.status
  if (status >= 500) {
    const errorId = error.response.data?.error_id
    error.response.data = {
      detail: GENERIC_SERVER_ERROR,
      ...(errorId ? { error_id: errorId } : {}),
    }
  }
  return error
}

// Best-effort clear of the JS-readable marker cookie. The backend normally
// clears it via Set-Cookie on /auth/logout, but a *failed* /auth/refresh
// doesn't emit Set-Cookie, so we expire it client-side too.
function clearLoggedInMarker(): void {
  if (typeof document === 'undefined') return
  const host = window.location.hostname
  // Match the backend's cookie domain: ".wetook.net" if we're on *.wetook.net,
  // otherwise host-only (covers localhost and standalone deployments).
  const domains = host.endsWith('.wetook.net') ? ['.wetook.net', host] : [host]
  for (const d of domains) {
    document.cookie = `wetook_logged_in=; Domain=${d}; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT`
  }
  document.cookie = 'wetook_logged_in=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT'
}

// Single in-flight refresh: concurrent 401s wait on the same /auth/refresh
// promise instead of each firing their own and racing to rotate the cookie.
let refreshPromise: Promise<void> | null = null

function refreshOnce(): Promise<void> {
  if (!refreshPromise) {
    refreshPromise = api
      .post('/auth/refresh')
      .then(() => {})
      .finally(() => { refreshPromise = null })
  }
  return refreshPromise
}

/**
 * How this app gets a session back when the cookie route cannot work.
 *
 * The default recovery is POST /auth/refresh, which relies on the HttpOnly
 * refresh COOKIE. That is right for every app served from *.wetook.net and
 * wrong for exactly one caller: the Android shell, whose pages come from
 * inside the APK on a different origin, making every wetook.net cookie
 * third-party — and WebView refuses to send those by default.
 *
 * A device there holds something better than a refresh cookie anyway: a device
 * token that a manager approved once and that outlives any session. Pocket
 * registers a recovery that spends it (see apps/pos/src/lib/deviceSession.ts),
 * so a till whose access token expired signs itself back in instead of being
 * bounced to a login page nobody is standing at.
 *
 * Left unset, nothing changes for anyone.
 */
let sessionRecovery: (() => Promise<boolean>) | null = null

export function setSessionRecovery(fn: (() => Promise<boolean>) | null): void {
  sessionRecovery = fn
}

/**
 * One recovery at a time. When a till's access token expires, every request
 * in flight comes back 401 together — the logs of 2026-10-20 show eight device
 * sessions minted inside three seconds for one tablet. They now all wait on
 * the same one.
 */
let recoveryPromise: Promise<boolean> | null = null

/** Try the registered recovery (a paired till re-spending its device token).
 *  False when there is none or it fails. Used by the 401 interceptor below and
 *  by AuthContext when its cookie-based proactive refresh fails, which inside
 *  the Android shell it always does: the refresh cookie is third-party there
 *  and never sent. */
export function recoverSession(): Promise<boolean> {
  if (!sessionRecovery) return Promise.resolve(false)
  if (!recoveryPromise) {
    const run = sessionRecovery
    recoveryPromise = (async () => {
      try {
        return await run()
      } catch {
        return false
      }
    })().finally(() => { recoveryPromise = null })
  }
  return recoveryPromise
}

/**
 * The Bearer token to send instead of relying on cookies, when one is set.
 *
 * Same single caller, same reason. A null here means "use the cookies", which
 * is what every browser-served app does.
 */
let bearerToken: string | null = null

export function setBearerToken(token: string | null): void {
  bearerToken = token
}

/**
 * Does this client hold a session by the Bearer route?
 *
 * Asked by AuthContext, which otherwise decides "is anyone signed in" by
 * looking for the `wetook_logged_in` marker COOKIE. That cookie is set for
 * wetook.net, so a client on another origin — the Android shell, serving its
 * pages from inside the APK — can never see one, no matter how valid its
 * session is. Without this the shell pairs successfully and is then bounced
 * straight to the login page it has no way to complete.
 */
export function hasBearerToken(): boolean {
  return bearerToken !== null
}

api.interceptors.request.use(config => {
  // Never overwrite an Authorization header a caller set deliberately — but a
  // header THIS interceptor stamped is ours, and is re-stamped every time.
  //
  // That distinction is the fix for "Pocket says unauthorized for no reason"
  // (2026-10-21). A retry after a 401 re-sends the same config object, which
  // still carried the EXPIRED token from its first attempt; "never overwrite"
  // then kept it, so every request replayed after a successful re-sign-in
  // failed again, the interceptor gave up and the till was sent to sign in —
  // with the order it was sending lost in between. Seen in production every
  // time a till's hourly access token ran out.
  const stamped = (config as any)._bearerStamped === true
  if (bearerToken && (stamped || !config.headers?.Authorization)) {
    config.headers = config.headers ?? {}
    ;(config.headers as any).Authorization = `Bearer ${bearerToken}`
    ;(config as any)._bearerStamped = true
  }
  return config
})

// On 401, try refresh once via the HttpOnly refresh cookie, then retry the
// original request. On terminal failure, redirect to the auth subdomain's
// /login (or local /login in dev). On 5xx, scrub the body.
api.interceptors.response.use(
  r => r,
  async error => {
    const original = error.config
    const detailCode = error.response?.data?.detail?.code
    if (error.response?.status === 403 && detailCode === 'account_inactive') {
      window.location.href = '/access-denied'
      return Promise.reject(sanitizeError(error))
    }
    // Never try to refresh on the refresh endpoint itself — that would loop.
    const isRefreshCall = typeof original?.url === 'string' && original.url.includes('/auth/refresh')

    // Device pairing is exempt, and not as a special case: a 401 from
    // /pos/pairing/* does not mean "your session expired", it means "this
    // device is not paired" — which is the normal state of a till out of a
    // box. Treating it as an expired session sends a device that has never
    // had a session to a login page, which is both wrong and unusable: there
    // is nobody at a counter with an account to type in. The caller handles
    // it by asking to be set up again.
    const isPairingCall = typeof original?.url === 'string' && original.url.includes('/pos/pairing/')

    if (error.response?.status === 401 && !original._retry && !isRefreshCall && !isPairingCall) {
      original._retry = true
      // A client on the Bearer route (the Android shell) has no refresh
      // cookie to send — the call was a guaranteed 401 on every expiry — so it
      // goes straight to its own recovery.
      if (!(bearerToken && sessionRecovery)) {
        try {
          await refreshOnce()
          return api(original)
        } catch {
          // fall through — the app may know another way back
        }
      }
      // A device that can re-authenticate itself gets to try before anyone is
      // sent to a login page. On a till this is the difference between a
      // cashier carrying on and a shop floor staring at a sign-in form.
      if (sessionRecovery) {
        if (await recoverSession()) return api(original)
        // The app that registered a recovery owns what happens next: a till
        // whose pairing was revoked goes to device setup (its recovery does
        // that), and one that merely could not REACH the server — offline, or
        // the Wi-Fi blinked — keeps its session and tries again on the next
        // call. Neither belongs on the web sign-in page: there is nobody at a
        // counter to type an account into it.
        return Promise.reject(sanitizeError(error))
      }
      // Terminal 401: session is gone. Clear the JS-readable marker so the
      // next page load doesn't trust a stale "logged in" hint.
      clearLoggedInMarker()
      // Don't redirect if already on an auth page — avoids infinite reload loops
      // on the auth app itself and prevents double-redirects in signage.
      const onAuthPage = ['/login', '/register', '/complete-registration', '/accept-invite', '/logout', '/forgot-password', '/reset-password']
        .some(p => window.location.pathname.startsWith(p))
      if (!onAuthPage) {
        const loginPath = AUTH_URL
          ? `${AUTH_URL}/login?callback=${encodeURIComponent(window.location.href)}`
          : '/login'
        window.location.href = loginPath
      }
    }
    return Promise.reject(sanitizeError(error))
  }
)

// ── Auth ─────────────────────────────────────────────────────────────
export const authLogin          = (data: object) => api.post('/auth/login',           data)
export const authRegister       = (data: object) => api.post('/auth/register',        data)
// Magic-link sign-up: step 1 emails a completion link (work email only); step 2
// posts the remaining account details from that link and issues the session.
export const authRegisterLink         = (data: object) => api.post('/auth/register-link',         data)
export const authCompleteRegistration = (data: object) => api.post('/auth/complete-registration', data)
// Google Sign-In: posts the Google ID token (credential). The backend verifies
// it and find-or-creates-or-links the user, then sets the same session cookies.
export const authGoogle         = (data: object) => api.post('/auth/google',          data)
export const authLogout         = ()             => api.post('/auth/logout')
// Revokes every session for the account (all devices/browsers) and bumps
// token_version, so the CURRENT device's access token dies too — callers must
// treat this exactly like a normal logout (clear state, redirect to /login).
export const authLogoutAll      = ()             => api.post('/auth/logout-all')
export const authMe             = ()             => api.get('/auth/me')
// /auth/refresh sends no body — the HttpOnly wetook_refresh cookie travels
// automatically because withCredentials is set on the axios instance.
export const authRefresh        = ()             => api.post('/auth/refresh')
export const authForgotPassword = (data: object) => api.post('/auth/forgot-password', data)
export const authResetPassword  = (data: object) => api.post('/auth/reset-password',  data)

// ── Dashboard ────────────────────────────────────────────────────────
export const getDashboardStats     = ()     => api.get('/dashboard/stats')
export const getScreenStatus       = ()     => api.get('/dashboard/screen-status')
export const getUptimeHistory      = ()     => api.get('/dashboard/uptime-history')
export const getContentStats       = ()     => api.get('/dashboard/content-stats')

// ── Screen Groups (Dashboard → My Groups tab) ─────────────────────────
// A ScreenGroup is a named bundle of screens belonging to an org. The
// list endpoint returns each group with `screens`, `online_count` and
// `total_count` pre-computed so the dashboard can render without N+1.
export const getScreenGroups   = ()                       => api.get('/screen-groups')
export const getScreenGroup    = (id: number)             => api.get(`/screen-groups/${id}`)
export const createScreenGroup = (data: object)           => api.post('/screen-groups', data)
export const updateScreenGroup = (id: number, data: object) => api.patch(`/screen-groups/${id}`, data)
export const deleteScreenGroup = (id: number)             => api.delete(`/screen-groups/${id}`)

// ── Screens ──────────────────────────────────────────────────────────
// Direct screen endpoints address screens by their opaque `public_id` (UUID),
// not the internal integer id — so dashboard URLs / API calls are
// non-enumerable. Bulk/relational helpers below (groups, branch membership,
// publish) still key on the integer id.
export const getScreens    = () => api.get('/screens')
export const getScreen     = (publicId: string) => api.get(`/screens/${publicId}`)
export const createScreen  = (d: any) => api.post('/screens', d)
export const updateScreen  = (publicId: string, d: any) => api.patch(`/screens/${publicId}`, d)
export const deleteScreen  = (publicId: string) => api.delete(`/screens/${publicId}`)
export const pairScreen    = (d: any) => api.post('/screens/pair', d)
export const getScreenLogs = (publicId: string) => api.get(`/screens/${publicId}/logs`)

// A screen's channel program: content / audio / stop, each a list of entries
// (live occupant + scheduled windows) with names resolved and an `active` flag.
// Backs the ScreenDetails channel cards under the single-occupant channel model.
export interface ChannelEntry {
  id:            number
  kind:          string
  content_id:    number | null
  content_name:  string | null
  start_at:      string | null
  end_at:        string | null
  scheduled:     boolean
  active:        boolean
}
export interface ScreenChannels {
  content: ChannelEntry[]
  audio:   ChannelEntry[]
  stop:    ChannelEntry[]
}
export const getScreenChannels = (publicId: string) =>
  api.get<ScreenChannels>(`/screens/${publicId}/channels`)

// Clear the content channel for a screen — the "remove content" X. Returns the
// screen to "No content assigned"; audio / stop channels are untouched.
export const clearScreenContent = (publicId: string) =>
  api.post(`/screens/${publicId}/clear-content`)

// Relink — move a paired Screen onto a different device row using the new
// device's pairing code. validateRelinkCode is the pre-flight check (so the
// confirm dialog can show "you're about to link to <device model>");
// relinkScreen performs the swap and broadcasts a content_update.
export interface RelinkValidateResponse {
  valid:            boolean
  device_model?:    string | null
  device_kind?:     'android' | 'web' | null
  new_device_name?: string | null
}
export const validateRelinkCode = (publicId: string, pairing_code: string) =>
  api.post<RelinkValidateResponse>(`/screens/${publicId}/validate-relink`, { pairing_code })
export const relinkScreen = (publicId: string, pairing_code: string) =>
  api.post(`/screens/${publicId}/relink`, { pairing_code })

// Reactivate — reconnect a screen whose license is ALREADY valid but that
// never auto-repaired after renewal. No code needed: unlike pairScreen (an
// org-agnostic code lookup, for claiming an anonymous new device), this
// always targets a screen the caller's org already owns (public_id), and the
// device_token already bound to it is already the right one — see
// screens_pairing.py:reactivate_screen for why a code would be redundant.
export const reactivateScreen = (publicId: string) =>
  api.post(`/screens/${publicId}/reactivate`)

// Ask for a screen's license to be renewed, optionally attaching a PO / quote /
// receipt. Multipart because of that file; the attachment lands in the PRIVATE
// bucket, so nothing here is a public URL. Persists to license_requests (the
// same admin queue as seat/access requests) and emails the admin.
// Release the hardware from a screen, keeping the screen, its content and its
// licence. For swapping or retiring a panel — the alternative used to be
// deleting the screen and rebuilding it.
export const detachScreen = (publicId: string) =>
  api.post(`/screens/${publicId}/detach`)

export const requestScreenRenewal = (
  publicId: string, reason: string, attachment?: File | null,
) => {
  const form = new FormData()
  if (reason.trim()) form.append('reason', reason.trim())
  if (attachment) form.append('attachment', attachment)
  return api.post(`/screens/${publicId}/renewal-request`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

// Flash an identification overlay on the physical TV — see PingOverlay /
// PingCard in the player apps. Backend rejects if the device isn't online.
export const pingScreen = (publicId: string) =>
  api.post(`/screens/${publicId}/ping`)
// Device commands (same online-only contract as ping — the backend 400s when
// the device isn't listening instead of silently queueing).
// resync: push a content_update so the device refetches its manifest now.
// restart-app: relaunch the player (web reloads the tab; android restarts
// the process). request-screenshot: android captures its own window and
// uploads via /devices/screenshot — N/A for web players.
export const resyncScreen = (publicId: string) =>
  api.post(`/screens/${publicId}/resync`)
export const restartScreenApp = (publicId: string) =>
  api.post(`/screens/${publicId}/restart-app`)
export const requestScreenshot = (publicId: string) =>
  api.post(`/screens/${publicId}/request-screenshot`)
// Two more of the device drawer's actions, made reachable without standing at
// the screen. check-updates is a command; auto-launch pushes a VALUE, because
// it is a device-local preference rather than a one-shot action.
//
// Note what is deliberately absent: "set as home app" and the OEM autostart
// allow-list both open system dialogs Android requires a human to accept on the
// device, and Exit would strand the screen with nothing able to restart it.
export const checkScreenUpdates = (publicId: string) =>
  api.post(`/screens/${publicId}/check-updates`)
export const setScreenAutoLaunch = (publicId: string, enabled: boolean) =>
  api.post(`/screens/${publicId}/auto-launch`, { enabled })
// Dashboard-side mirror of /devices/content/{token} — used by the Sync
// preview tab on ScreenDetailsPage to render exactly what the device should
// be showing right now, plus server_now/last_seen for client-side offset.
export interface SyncContentItem {
  id:              number
  type:            string
  name:            string
  duration?:       number | null
  url?:            string | null
  render_url?:     string | null
  is_interactive?: boolean
}
// Zoned layout mirror of the device manifest's ContentLayout (devices.py).
// Present only when a layout is published to the screen; `items` is empty in
// that case and content lives across these zones instead.
export interface SyncContentZone {
  id:         string
  name:       string
  col:        number
  row:        number
  col_span:   number
  row_span:   number
  items:      SyncContentItem[]
}
export interface SyncContentLayout {
  id:             number
  name:           string
  template:       string
  cols:           number
  rows:           number
  column_weights: number[]
  row_weights:    number[]
  zones:          SyncContentZone[]
}
export interface ScreenContentSync {
  content: {
    screen_id:    number
    orientation:  string
    is_paired:    boolean
    is_active:    boolean
    items: SyncContentItem[]
    layout?: SyncContentLayout | null
  }
  server_now:  string   // ISO 8601
  last_seen:   string | null
}
export const getScreenContent = (publicId: string) =>
  api.get<ScreenContentSync>(`/screens/${publicId}/content`)

// ── Visual menu: removed 2026-10-08 ──────────────────────────────────
// The visual menu (MenusPage / MenuDetailsPage) was dormant since May and has
// been dropped. The backend Menu entity stays: it stores Interactive Links
// (type='interactive') — see createInteractiveMenu below and Media → Links.

// ── Branches ─────────────────────────────────────────────────────────
// Branch is the unified entity (Spaces were merged in): it carries both the
// physical-location fields (managed in the menu app) AND the workspace facets
// — members + grouped screens/pings (managed in the account app).
export type BranchRole = 'viewer' | 'editor' | 'owner'
export type BranchMember = {
  user_id: number
  branch_id: number
  role: BranchRole
  user_name: string
  user_email: string
}
export type Branch = {
  id: number
  name: string
  name_ar?: string
  description?: string
  is_active: boolean
  minimum_order?: number
  open_time?: string
  close_time?: string
  created_at: string
  members: BranchMember[]
  screen_ids: number[]
  ping_ids: number[]
}

export const getBranches           = ()                => api.get<Branch[]>('/branches')
export const createBranch          = (data: object)    => api.post<Branch>('/branches', data)
export const getBranch             = (id: number)      => api.get<Branch>(`/branches/${id}`)
export const updateBranch          = (id: number, data: object) => api.patch<Branch>(`/branches/${id}`, data)
export const deleteBranch          = (id: number)      => api.delete(`/branches/${id}`)

// Branch membership (absorbed from Spaces).
export const getBranchMembers   = (id: number)                                       => api.get<BranchMember[]>(`/branches/${id}/members`)
export const addBranchMember    = (id: number, data: { user_id: number; role: BranchRole }) => api.post<BranchMember>(`/branches/${id}/members`, data)
export const updateBranchMember = (id: number, userId: number, role: BranchRole)     => api.patch<BranchMember>(`/branches/${id}/members/${userId}`, { role })
export const removeBranchMember = (id: number, userId: number)                       => api.delete(`/branches/${id}/members/${userId}`)

// Screen ↔ branch assignment. A screen belongs to exactly one branch; assigning
// repoints its branch_id. The transfer flow on the screen-detail page calls
// assign-to-new (idempotent — no separate remove needed since it's a move).
export const assignScreenToBranch   = (branchId: number, screenId: number) =>
  api.post(`/branches/${branchId}/screens/${screenId}`)
export const unassignScreenFromBranch = (branchId: number, screenId: number) =>
  api.delete(`/branches/${branchId}/screens/${screenId}`)

// ── Products ─────────────────────────────────────────────────────────
export const getCategories         = ()                => api.get('/products/categories')
export const createCategory        = (data: object)    => api.post('/products/categories', data)
export const updateCategory        = (id: number, data: object) => api.patch(`/products/categories/${id}`, data)
export const deleteCategory        = (id: number)      => api.delete(`/products/categories/${id}`)
export const getTaxGroups          = ()                => api.get('/products/tax-groups')
export const getProducts           = (params?: object) => api.get('/products', { params })
export const createProduct         = (data: object)    => api.post('/products', data)
export const updateProduct         = (id: number, data: object) => api.patch(`/products/${id}`, data)
export const deleteProduct         = (id: number)      => api.delete(`/products/${id}`)
export const downloadProductTemplate = ()              => api.get('/products/import/template', { responseType: 'blob' })
/** The whole catalogue as a CSV the importer accepts back â€” see the route. */
export const exportProducts        = ()                => api.get('/products/export', { responseType: 'blob' })
export const importProducts        = (file: File)      => {
  const form = new FormData()
  form.append('file', file)
  return api.post('/products/import', form, { headers: { 'Content-Type': 'multipart/form-data' } })
}

// ── Media ────────────────────────────────────────────────────────────
// `archived` toggles between the live library (default) and the Archive.
// `sourceApp` scopes the list to one app's own uploads ('signage' | 'ping' |
// 'my') — every app shares this table (see uploadMedia below), so a picker
// that wants only ITS OWN content — not another app's uploads mixed in —
// must pass its app key. Omit to get the old unfiltered behaviour.
export const getMedia              = (archived = false, sourceApp?: string) =>
  api.get('/media', { params: { archived, source_app: sourceApp } })
export const uploadMedia           = async (
  file: File, onProgress?: (pct: number) => void,
  // Which app this upload belongs to — stamped on the row so per-app
  // pickers (getMedia's sourceApp) can tell it apart from every other app's
  // uploads sharing this same table. Defaults to 'signage' server-side
  // (Frame's own library) when omitted.
  sourceApp?: 'signage' | 'ping' | 'my',
  signal = getUploadSignal(file),
) => {
  assertUploadSize(file, MEDIA_UPLOAD_LIMIT_BYTES)
  // Enforce the image-format allowlist (PNG/WebP/AVIF/SVG) for any image
  // upload. Non-image media (video, fonts, pdf) passes through untouched; the
  // backend is the authoritative gate, this just fails fast with a clear error.
  if (!isAllowedImageType(file)) {
    throw new Error('Only PNG, WebP, AVIF, or SVG images are allowed.')
  }
  // Each attempt builds a fresh FormData — a multipart body shouldn't be reused
  // across sends (and the global 401 interceptor can't cleanly replay one).
  const send = () => {
    const form = new FormData()
    form.append('file', file)
    if (sourceApp) form.append('source_app', sourceApp)
    return api.post('/media/upload', form, {
      signal,
      timeout: file.type.startsWith('image/') ? 120000 : undefined,
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
        ? (e) => { if (e.total) onProgress(Math.round((e.loaded / e.total) * 100)) }
        : undefined,
    })
  }
  try {
    return await send()
  } catch (err: any) {
    if (signal?.aborted || err?.code === 'ERR_CANCELED') throw err
    // The first upload can fail when it races an access-token refresh: the
    // interceptor refreshes + retries, but its retry of a multipart request
    // doesn't replay cleanly, so the call rejects even though the session is
    // now valid. A single fresh retry then succeeds — this is the "fails once,
    // works the second time" behaviour, handled automatically. Don't retry
    // deterministic client errors (415 unsupported type, 413 too large).
    const status = err?.response?.status
    if (status && status >= 400 && status < 500 && status !== 401) throw err
    return await send()
  }
}
export const updateMedia           = (id: number, data: { original_name?: string; duration?: number }, opts?: { initial?: boolean }) =>
  // `initial`: naming applied by the upload flow itself — not logged as a rename.
  api.patch(`/media/${id}`, data, opts?.initial ? { params: { initial: true } } : undefined)
// Soft-delete (move to Archive) / restore. Hard delete is only honoured by the
// backend once the item is archived.
export const archiveMedia          = (id: number)       => api.post(`/media/${id}/archive`)
export const restoreMedia          = (id: number)       => api.post(`/media/${id}/restore`)
export const deleteMedia           = (id: number)       => api.delete(`/media/${id}`)
// Publish a single media file directly to screens and/or groups. Permanent
// override — the asset becomes the target screen's sole content (mirrors the
// no-window publishPlaylist).
// Publish a single media file to screens and/or groups. Omit start_at/end_at
// for a permanent publish (overwrites other content); include them for a
// scheduled (windowed) publish.
export const publishMedia          = (
  id: number,
  data: { screen_ids?: number[]; group_ids?: number[]; start_at?: string | null; end_at?: string | null },
) => api.post(`/media/${id}/publish`, data)

// ── Tasks (Screen Job Tasks feed) ──────────────────────────────────────
// Server-tracked operator activity. Publish tasks carry per-screen rows that
// stay `pending` until each screen's device actually pulls the new content.
export type TaskScreenStatus = 'pending' | 'applied'
export type TaskStatus = 'pending' | 'running' | 'done' | 'failed'
export type TaskType =
  | 'publish_playlist' | 'publish_layout' | 'publish_media' | 'publish_menu'
  | 'publish_audio' | 'publish_audio_playlist' | 'publish_stop_screen' | 'publish_transition'
  // Scheduled removal — the sibling task a windowed publish spawns to record the
  // content coming off the screen when its end_at arrives (Pending→Running→Done).
  | 'content_expire'
  | 'upload' | 'archive' | 'restore' | 'rename'
  | 'screen_activate' | 'screen_deactivate'
  // License lifecycle + connectivity — recorded as activity so they show in the
  // Activity Center feed / bell (see backend TASK_LICENSE_TYPES / TASK_STATUS_TYPES).
  | 'license_granted' | 'license_renewed' | 'license_expired' | 'license_freed'
  | 'screen_online' | 'screen_offline'
export type TaskScreen = {
  id: number
  screen_id: number
  screen_name: string | null
  // Non-enumerable screen UUID for deep-linking (/screens/{public_id});
  // null on rows created before the column existed.
  screen_public_id?: string | null
  status: TaskScreenStatus
  published_at: string
  applied_at: string | null
}
export type Task = {
  id: number
  type: TaskType
  target_label: string
  status: TaskStatus
  progress: number
  // Publish tasks carry {kind, id} of the published content so the feed can
  // link back to it (kind ∈ playlist|layout|media|menu|audio|audio_playlist).
  meta?: Record<string, unknown> | null
  actor_id: number | null
  // Denormalized at creation; null on pre-migration rows or system tasks.
  actor_name?: string | null
  created_at: string
  updated_at: string
  screens: TaskScreen[]
}
export const getTasks = (params?: { status?: string; limit?: number }) =>
  api.get<Task[]>('/tasks', { params })
// Clears the org's finished activity (done + failed); in-flight tasks are kept.
export const clearTasks = () =>
  api.delete<{ deleted: number }>('/tasks')
// CSV of the org's activity — one row per task-screen, full history (not the
// live feed's cap). Filters mirror the Activity Center so the file matches the
// on-screen view. Returns a blob for a client-side download.
export const exportTasks = (params?: { status?: string; type?: string; q?: string; branch_id?: number; include_status?: boolean }) =>
  api.get('/tasks/export', { params, responseType: 'blob' })

// ── Notifications (bell backfill) ────────────────────────────────────
// Attention events (offline alerts, licence warnings/expiries, failed
// publishes) from the last 7 days, newest first — what the bell missed while no
// dashboard was open. Ids are stable and match the live WS events'
// notification_id, so the bell merges the two without duplicates.
export type BellEvent = {
  id: string
  kind: 'screen_offline_alert' | 'screen_license_expiring' | 'screen_license_expired' | 'task_failed'
  data: Record<string, unknown>
  created_at: string
}
export const getNotifications = (params?: { since?: string; limit?: number }) =>
  api.get<BellEvent[]>('/notifications', { params })

// ── Audio ────────────────────────────────────────────────────────────
// Background-music tracks attached to playlists. Same two-phase upload
// shape as /media so we can reuse the patterns on the call sites.
export const getAudios             = (archived = false) => api.get('/audios', { params: { archived } })
export const uploadAudio           = (file: File, onProgress?: (pct: number) => void) => {
  // Video-as-audio uses the media-sized backend allowance.
  assertUploadSize(file, file.type.startsWith('video/') ? MEDIA_UPLOAD_LIMIT_BYTES : AUDIO_UPLOAD_LIMIT_BYTES)
  const form = new FormData()
  form.append('file', file)
  return api.post('/audios/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress
      ? (e) => { if (e.total) onProgress(Math.round((e.loaded / e.total) * 100)) }
      : undefined,
  })
}
export const updateAudio           = (id: number, data: { original_name?: string; duration?: number }) => api.patch(`/audios/${id}`, data)
export const archiveAudio          = (id: number)       => api.post(`/audios/${id}/archive`)
export const restoreAudio          = (id: number)       => api.post(`/audios/${id}/restore`)
export const deleteAudio           = (id: number)       => api.delete(`/audios/${id}`)
// Stream a single audio track to screens and/or groups. Overrides whatever
// audio was streaming on each target (visual content is left untouched).
export const publishAudio          = (
  id: number,
  data: { screen_ids?: number[]; group_ids?: number[]; start_at?: string | null; end_at?: string | null },
) => api.post(`/audios/${id}/publish`, data)
// Clear the streamed audio on the target screens/groups.
export const stopAudioStream       = (
  data: { screen_ids?: number[]; group_ids?: number[] },
) => api.post('/audios/stop-stream', data)

// ── Audio playlists (Audio Hub) ───────────────────────────────────────
export const getAudioPlaylists     = ()                 => api.get('/audio-playlists')
export const getAudioPlaylist      = (id: number)       => api.get(`/audio-playlists/${id}`)
export const createAudioPlaylist   = (data: object)     => api.post('/audio-playlists', data)
export const updateAudioPlaylist   = (id: number, data: object) => api.patch(`/audio-playlists/${id}`, data)
export const deleteAudioPlaylist   = (id: number)       => api.delete(`/audio-playlists/${id}`)
// Mirror of publishAudio — stream an audio playlist to screens and/or groups.
export const publishAudioPlaylist  = (
  id: number,
  data: { screen_ids?: number[]; group_ids?: number[]; start_at?: string | null; end_at?: string | null },
) => api.post(`/audio-playlists/${id}/publish`, data)

// ── Playlists ─────────────────────────────────────────────────────────
export const getPlaylists          = ()                 => api.get('/playlists')
export const getPlaylist           = (id: number)       => api.get(`/playlists/${id}`)
export const createPlaylist        = (data: object)     => api.post('/playlists', data)
export const updatePlaylist        = (id: number, data: object) => api.patch(`/playlists/${id}`, data)
export const deletePlaylist        = (id: number)       => api.delete(`/playlists/${id}`)
// Assign a playlist to screens and/or groups. Omit start_at/end_at for a
// permanent publish; include them for a scheduled (windowed) publish.
export const publishPlaylist       = (
  id: number,
  data: { screen_ids?: number[]; group_ids?: number[]; start_at?: string | null; end_at?: string | null },
) => api.post(`/playlists/${id}/publish`, data)

// ── Signage Layouts (Content Hub → Layouts tab) ──────────────────────
// Separate from /layouts which serves the Ping builder library; the
// `signage-` prefix keeps the two routers from stepping on each other.
export const getSignageLayouts     = ()                 => api.get('/signage-layouts')
export const getSignageLayout      = (id: number)       => api.get(`/signage-layouts/${id}`)
export const createSignageLayout   = (data: object)     => api.post('/signage-layouts', data)
export const updateSignageLayout   = (id: number, data: object) => api.patch(`/signage-layouts/${id}`, data)
export const deleteSignageLayout   = (id: number)       => api.delete(`/signage-layouts/${id}`)
// Mirror of publishPlaylist — Publish (no window) is permanent and clears
// other content sources on the target screens; Schedule sends start_at/end_at
// for a windowed publish.
export const publishSignageLayout  = (
  id: number,
  data: { screen_ids?: number[]; group_ids?: number[]; start_at?: string | null; end_at?: string | null },
) => api.post(`/signage-layouts/${id}/publish`, data)

// ── Stop Screens (Content Hub → Stop Screens tab) ────────────────────
// Interactive attract overlay (images/videos + image/video playlists) that
// paints on top of the normal content and dismisses on touch. Publish is a
// NON-destructive layer assignment; unpublish removes the overlay from screens.
export const getStopScreens        = ()                 => api.get('/stop-screens')
export const getStopScreen         = (id: number)       => api.get(`/stop-screens/${id}`)
export const createStopScreen      = (data: object)     => api.post('/stop-screens', data)
export const updateStopScreen      = (id: number, data: object) => api.patch(`/stop-screens/${id}`, data)
export const deleteStopScreen      = (id: number)       => api.delete(`/stop-screens/${id}`)
export const publishStopScreen     = (
  id: number,
  data: { screen_ids?: number[]; group_ids?: number[]; start_at?: string | null; end_at?: string | null },
) => api.post(`/stop-screens/${id}/publish`, data)
export const unpublishStopScreen   = (
  id: number,
  data: { screen_ids?: number[]; group_ids?: number[] },
) => api.post(`/stop-screens/${id}/unpublish`, data)

// ── Mixes (Content Hub → Lab tab) ────────────────────────────────────
// A Mix bundles a main content item (playlist OR layout, mutually exclusive)
// with companions (audio / audio playlist / stop screen / transition) as a
// standalone, named entity. CRUD-only — publishing a mix is orchestrated
// client-side (replays the per-kind publish endpoints with the mix's picks +
// window), same as inline companions.
export interface Mix {
  id:                  number
  org_id:              number
  name:                string
  playlist_id:         number | null
  layout_id:           number | null
  audio_id:            number | null
  audio_playlist_id:   number | null
  stop_screen_id:      number | null
  transition:          string | null
  created_at:          string
  updated_at:          string | null
  playlist_name?:      string | null
  layout_name?:        string | null
  audio_name?:         string | null
  audio_playlist_name?: string | null
  stop_screen_name?:   string | null
}
export const getMixes    = ()                       => api.get<Mix[]>('/mixes')
export const getMix      = (id: number)             => api.get<Mix>(`/mixes/${id}`)
export const createMix   = (data: { name: string; playlist_id?: number | null; layout_id?: number | null; audio_id?: number | null; audio_playlist_id?: number | null; stop_screen_id?: number | null; transition?: string | null }) => api.post<Mix>('/mixes', data)
export const updateMix   = (id: number, data: object) => api.patch<Mix>(`/mixes/${id}`, data)
export const deleteMix   = (id: number)             => api.delete(`/mixes/${id}`)

// Transitions — the content-item animation is a per-screen display setting.
// Publishing sets transition + speed on each target screen (backend merges into
// display_settings) and records a publish_transition activity task. An empty /
// 'default' key clears the override (players fall back to the fade crossfade).
export const publishTransition = (data: {
  key: string
  name?: string
  speed?: 'slow' | 'normal' | 'fast'
  screen_ids?: number[]
  group_ids?: number[]
}) => api.post('/transitions/publish', data)

// Server-side: each PDF page is rendered to a PNG, stored as a MediaFile, and
// bundled into a new Playlist. The returned playlist can be assigned to a
// screen via PATCH /screens/{id} like any other playlist.
export const createPlaylistFromPdf = (file: File, name: string, pageDuration = 10) => {
  const form = new FormData()
  form.append('file', file)
  form.append('name', name)
  form.append('page_duration', String(pageDuration))
  return api.post('/playlists/from-pdf', form, { headers: { 'Content-Type': 'multipart/form-data' } })
}

// Server-side: each PDF page is rendered to a PNG MediaFile and the bare list
// is returned (no playlist). The caller files them into a folder so the PDF
// lands as a "folder of images".
export const createMediaFromPdf = (file: File, name: string) => {
  const form = new FormData()
  form.append('file', file)
  form.append('name', name)
  return api.post('/media/from-pdf', form, { headers: { 'Content-Type': 'multipart/form-data' } })
}

// ── Folders (Assets library) ──────────────────────────────────────────
// Persistent, org-scoped folders for the signage Assets page (previously
// browser-local). `kind` is 'm' (media) | 'a' (audio) | 'l' (link).
export type FolderItemKind = 'm' | 'a' | 'l'
// GET returns { folders, items } where items maps "${kind}:${id}" → folder id.
export const getFolders   = ()                                              => api.get('/folders')
export const createFolder = (data: { name: string; parent_id?: string | null }) => api.post('/folders', data)
export const updateFolder = (id: string, data: { name?: string; parent_id?: string | null }) => api.patch(`/folders/${id}`, data)
export const deleteFolder = (id: string)                                    => api.delete(`/folders/${id}`)
export const assignFolder = (data: { folder_id: string | null; items: { kind: FolderItemKind; item_id: number }[] }) =>
  api.post('/folders/assign', data)

// Creates an Interactive Link (a Menu row with type='interactive'). Returned
// menu has `interactive_link` set; assign it to a screen via the regular
// PATCH /screens menu_ids flow.
//
// Also used by the Asset → Links tab; the optional show_qr / qr_position
// fields drive a QR overlay rendered on the live page during playback.
export type QrPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
export const createInteractiveMenu = (
  name: string,
  url: string,
  opts?: { show_qr?: boolean; qr_position?: QrPosition },
) =>
  api.post('/menus', {
    name,
    type: 'interactive',
    interactive_link: url,
    show_qr: opts?.show_qr ?? false,
    qr_position: opts?.qr_position ?? 'bottom-right',
  })
export const updateInteractiveMenu = (
  id: number,
  data: { name?: string; interactive_link?: string; show_qr?: boolean; qr_position?: QrPosition },
) => api.patch(`/menus/${id}`, data)
export const deleteInteractiveMenu = (id: number) => api.delete(`/menus/${id}`)
// Publish a single menu / interactive link directly to screens and/or groups.
// Mirrors publishMedia: omit start_at/end_at for a permanent overwrite; include
// them for a scheduled (windowed) publish.
export const publishMenu           = (
  id: number,
  data: { screen_ids?: number[]; group_ids?: number[]; start_at?: string | null; end_at?: string | null },
) => api.post(`/menus/${id}/publish`, data)

// ── Users ─────────────────────────────────────────────────────────────
export const getUsers              = ()                 => api.get('/users')
export const createUser            = (data: object)     => api.post('/users', data)
export const updateUser            = (id: number, data: object) => api.patch(`/users/${id}`, data)
export const deleteUser            = (id: number)       => api.delete(`/users/${id}`)

// ── Visuals ───────────────────────────────────────────────────────────
export const validatePairingCode   = (code: string)     => api.get(`/devices/validate/${code}`)
export const getVisuals            = ()                 => api.get('/visuals')
export const createVisual          = (data: object)     => api.post('/visuals', data)
export const updateVisual          = (id: number, data: object) => api.patch(`/visuals/${id}`, data)
export const deleteVisual          = (id: number)       => api.delete(`/visuals/${id}`)

// ── Brand Assets ─────────────────────────────────────────────────────
export const getBrandAssets        = ()                              => api.get('/brand-assets')
export const updateBrandAsset      = (slotKey: string, url: string | null) => api.patch(`/brand-assets/${slotKey}`, { url })

// ── License-expiry fallback image (Frame, enterprise-only) ────────────
// One image per org, shown on every screen instead of the standard
// unpair -> pairing-code flow when a screen's license expires. `entitled`
// tells the Settings page whether to render the upload control or an
// upgrade prompt — see backend/app/api/routes/settings_routes.py.
export interface LicenseFallbackImage { url: string | null; entitled: boolean }
export const getLicenseFallbackImage    = ()                    => api.get<LicenseFallbackImage>('/settings/license-fallback-image')
export const updateLicenseFallbackImage = (url: string | null)  => api.patch<LicenseFallbackImage>('/settings/license-fallback-image', { url })

// ── Admin ─────────────────────────────────────────────────────────────
export const getAdminStats         = ()                 => api.get('/admin/stats')
export const getAdminOrders        = ()                 => api.get('/admin/orders')
export const getAdminScreens       = ()                 => api.get('/admin/screens')
export const getAdminActivity      = ()                 => api.get('/admin/activity')

// ── Product Groups ───────────────────────────────────────────────────
export const getProductGroups         = ()                              => api.get('/products/groups')
export const createProductGroup       = (data: object)                  => api.post('/products/groups', data)
export const updateProductGroup       = (id: number, data: object)      => api.patch(`/products/groups/${id}`, data)
export const deleteProductGroup       = (id: number)                    => api.delete(`/products/groups/${id}`)
export const addProductsToGroup       = (id: number, product_ids: number[]) => api.post(`/products/groups/${id}/items`, { product_ids })
export const removeProductFromGroup   = (id: number, product_id: number)    => api.delete(`/products/groups/${id}/items/${product_id}`)

// ── Extras ───────────────────────────────────────────────────────────
export const getExtras             = ()                 => api.get('/extras')
export const updateExtraOption     = (groupId: number, optionId: number, data: object) => api.patch(`/extras/${groupId}/options/${optionId}`, data)
export const downloadExtrasTemplate = ()               => api.get('/extras/import/template', { responseType: 'blob' })
/** Groups + options as the same two-CSV zip the template ships. */
export const exportExtras          = ()                 => api.get('/extras/export', { responseType: 'blob' })
export const importExtras          = (groupsFile: File | null, optionsFile: File | null) => {
  const form = new FormData()
  if (groupsFile)  form.append('groups_file',  groupsFile)
  if (optionsFile) form.append('options_file', optionsFile)
  return api.post('/extras/import', form, { headers: { 'Content-Type': 'multipart/form-data' } })
}

// ── Settings ─────────────────────────────────────────────────────────
export const getProfile            = ()                 => api.get('/settings/profile')
export const updateProfile         = (data: object)     => api.patch('/settings/profile', data)

// ── Onboarding ───────────────────────────────────────────────────────
// First-run org setup (org name → logo → contact details). `/complete` writes
// the canonical Organization.name + settings and flips org.is_onboarded.
export const getOnboardingStatus   = ()                 => api.get('/onboarding/status')
export const completeOnboarding    = (data: object)     => api.post('/onboarding/complete', data)

// ── Invites ───────────────────────────────────────────────────────────
export const sendInvite   = (data: { email: string; name?: string; role: string; app_scope?: string[]; app_roles?: Record<string, string>; branch_ids?: number[] }) => api.post('/invites', data)
// Roles + scopable apps + permission→app map for the team-management UI.
export const getAccessCatalog = () => api.get('/users/access-catalog')
export const listInvites  = ()           => api.get('/invites')
export const revokeInvite = (id: number) => api.delete(`/invites/${id}`)

// ── Offers ────────────────────────────────────────────────────────────
export const getOffers    = ()                              => api.get('/offers')
export const createOffer  = (data: object)                  => api.post('/offers', data)
export const updateOffer  = (id: number, data: object)      => api.patch(`/offers/${id}`, data)
export const deleteOffer  = (id: number)                    => api.delete(`/offers/${id}`)

// ── Org-level Subscription (plan tier + free-trial status) ────────────────
// Distinct from AppSubscription below (per-app seats/expiry) — this is the
// account-wide plan/trial axis. "my follows whatever other app has
// subscription": my has no gate of its own, it just displays this + each
// app's own AppSubscription state (see SubscriptionPage.tsx).
export interface Subscription {
  id:                    number
  org_id:                number
  plan:                  string          // 'free' | 'pro' | 'enterprise'
  status:                string          // 'active' | 'trialing' | 'suspended' | 'cancelled' | 'expired'
  trial_ends_at:         string | null
  current_period_start:  string | null
  current_period_end:    string | null
  cancelled_at:          string | null
  created_at:            string
  updated_at:            string | null
}
export const getSubscription    = ()               => api.get<Subscription>('/subscriptions/current')
export const updateSubscription = (data: Partial<Pick<Subscription, 'plan' | 'status' | 'trial_ends_at'>>) =>
  api.patch<Subscription>('/subscriptions/current', data)

// ── App subscriptions (per-app entitlements: seat count + expiry) ─────────
// User-facing reads; admin-facing writes live further down (admin* wrappers).
export interface AppSubscription {
  id:              number
  org_id:          number
  app_kind:        string          // 'signage' | 'studio' | ...
  seat_count:      number
  expires_at:      string | null
  status:          string          // 'active' | 'suspended' | 'cancelled' | 'inactive'
  seats_used:      number
  seats_remaining: number
  // Earliest upcoming per-license expiry (signage). NULL = all perpetual / none.
  next_license_expiry?: string | null
  /**
   * Pocket only: when a lapsed shop's tills stop selling.
   *
   * An expired Cashier subscription does not stop the tills at once — they
   * trade for a grace period so a lapse nobody noticed until Monday does not
   * close the shop mid-service. NULL for a healthy subscription, for a lapse
   * with no expiry to measure from, and for every app that is not Pocket.
   *
   * Read from THIS route rather than a /pos one on purpose: by the time it
   * matters, every /pos route refuses a back-office caller.
   */
  till_grace_ends_at?: string | null
  created_at:      string
  updated_at:      string | null
}
export const listAppSubscriptions   = ()                  => api.get<AppSubscription[]>('/app-subscriptions')
export const getAppSubscription     = (appKind: string)   => api.get<AppSubscription>(`/app-subscriptions/${appKind}`)
// Per-seat breakdown (which screen holds which license + its own expiry) —
// only signage returns rows today; other app_kinds resolve to []. Backs the
// subscription detail page's license table (my.wetook → Subscriptions).
export interface AppSeatLicense {
  id:             number
  purchased_at:   string | null
  duration_days:  number | null
  activated_at:   string | null
  expires_at:     string | null
  /** Signage: the screen holding this seat. Null when unclaimed. */
  screen_id:      number | null
  screen_name:    string | null
  /** Cashier: the terminal holding this seat. Null when unclaimed. */
  terminal_id?:   number | null
  terminal_name?: string | null
  /** Cashier only: which pool (main | secondary | kds). Registers,
   *  order-takers and kitchen displays are bought and activated separately,
   *  so an org's Cashier seats are three pools, not one list. */
  kind?:          string | null
}
export const getAppLicenses         = (appKind: string)   => api.get<AppSeatLicense[]>(`/app-subscriptions/${appKind}/licenses`)
// Self-service request — either "more seats of an app we have" (pass
// quantity) or "turn this app on for us" (omit it — the org-entitlement
// no-access page's Request access button). Admin gets notified either way,
// no immediate state change. 202 Accepted on success.
export const requestAppLicenses     = (
  appKind: string,
  // `seat_kind` is Cashier-only: a register, an order-taker and a kitchen
  // display are separate pools, so "2 more seats" is not an answerable ask
  // there. Every other app has one undifferentiated seat and omits it.
  data:    { quantity?: number; reason?: string; seat_kind?: string },
) => api.post<{ ok: boolean; message: string }>(`/app-subscriptions/${appKind}/request`, data)

// Admin endpoints (app-subscriptions) live on the admin JWT instance, see
// packages/ui/src/lib/adminApi.ts. They're called directly from the admin
// dashboard as adminApi.get('/admin/app-subscriptions') — kept out of this
// file so the user-JWT instance can't accidentally hit them.

// Public endpoints — no auth header, separate instance
const publicApi = axios.create({ baseURL: (BACKEND_URL || '') + '/api/v1' })
export const getInviteInfo          = (token: string) => publicApi.get(`/invites/accept/${token}`)
export const acceptInvite           = (token: string, data: { name: string; password: string }) =>
  publicApi.post(`/invites/accept/${token}`, data)
export const getPublicPing        = (slug: string)  => publicApi.get(`/pings/public/${slug}`)
export const getPublicMenuProducts  = (slug: string)  => publicApi.get(`/pings/public/${slug}/products`)
export const getPublicPlaceholders  = (slug: string)  => publicApi.get(`/pings/public/${slug}/placeholders`)
export const getPublicBranches      = (slug: string)  => publicApi.get(`/pings/public/${slug}/branches`)
export const submitFeedback         = (slug: string, data: object) => publicApi.post(`/pings/public/${slug}/feedback`, data)
// Storefront customer auth is WhatsApp-OTP: request a code for a phone, then
// verify it (with a name for first-time customers) to receive a customer token.
export const requestCustomerOtp     = (slug: string, phone: string) =>
  publicApi.post(`/pings/public/${slug}/customer/request-otp`, { phone })
export const verifyCustomerOtp      = (slug: string, phone: string, code: string, name?: string, whatsappOptIn = false) =>
  publicApi.post(`/pings/public/${slug}/customer/verify-otp`, { phone, code, name, whatsapp_opt_in: whatsappOptIn })
// Price a basket as the till will (options, availability, automatic offers)
// without placing an order — what the storefront basket shows.
export const quotePublicBasket      = (slug: string, data: object) =>
  publicApi.post(`/pings/public/${slug}/quote`, data)
export const submitPublicOrder      = (slug: string, data: object, customerToken: string) =>
  publicApi.post(`/pings/public/${slug}/orders`, data, {
    headers: { Authorization: `Bearer ${customerToken}` },
  })
export const getPublicCustomerOrders = (slug: string, customerToken: string) =>
  publicApi.get(`/pings/public/${slug}/my-orders`, {
    headers: { Authorization: `Bearer ${customerToken}` },
  })
export const getPublicCustomerProfile = (slug: string, customerToken: string) =>
  publicApi.get(`/pings/public/${slug}/customer/me`, {
    headers: { Authorization: `Bearer ${customerToken}` },
  })
export const updatePublicCustomerProfile = (slug: string, data: object, customerToken: string) =>
  publicApi.patch(`/pings/public/${slug}/customer/me`, data, {
    headers: { Authorization: `Bearer ${customerToken}` },
  })
// DSAR (PDPL): export everything we hold, or erase the account (hybrid:
// hard-delete with no orders, anonymize-and-retain when orders exist).
export const exportCustomerData = (slug: string, customerToken: string) =>
  publicApi.get(`/pings/public/${slug}/customer/export`, {
    headers: { Authorization: `Bearer ${customerToken}` },
  })
export const deleteCustomerAccount = (slug: string, customerToken: string) =>
  publicApi.delete(`/pings/public/${slug}/customer/me`, {
    headers: { Authorization: `Bearer ${customerToken}` },
  })

// Processor DPA (PDPL): org-owner acceptance surfaced in the dashboard banner.
export const getDpaStatus = () => api.get('/settings/dpa')
export const acceptDpa    = () => api.post('/settings/dpa/accept')

export default api
